import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { CratesDataSourceService } from '../data-sources/crates-data-source.service';
import { MissingCrate } from 'src/models/Crate';

const PAGE_SIZE_OPTIONS = [30, 60, 90];
export interface MissingCrateSortParams {
  sortBy: "ASC" | "DESC",
  orderBy: "activeInventoryCount" | "receivedAt" | "inventoryCount" | "lastMovedAt" | "sku" | "quantity" | "tagRfid" | "updatedAt" | "alias" | "section"
}

export interface MissingCrateFilterParams {
  receivedAtEndDate?: number;
  receivedAtStartDate?: number;
  lastMovedAtStartDate?: number,
  lastMovedAtEndDate?: number
  quantity?: number,
  inventoryCount?: number,
  maxActiveInventoryCount?: number,
  minActiveInventoryCount?: number,
  sku?: string,
  tagRfid?: string;
  section?: string;
  alias?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MissingCratesService {
  private $pageSize: BehaviorSubject<number> = new BehaviorSubject<number>(PAGE_SIZE_OPTIONS[0]);
  private $totalItems: Subject<number> = new Subject<number>();
  private $pageCount: Subject<number> = new Subject<number>();
  public $currentPage: Subject<number> = new Subject<number>();
  private $atLastPage: Subject<boolean> = new Subject<boolean>();
  private $atFirstPage: Subject<boolean> = new Subject<boolean>();
  private $items: Subject<any[]> = new Subject<any[]>();
  private $pageSizeOptions: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  private pageSize = PAGE_SIZE_OPTIONS[0];
  private pageSizeOptions = PAGE_SIZE_OPTIONS;
  private pageCount;
  public currentPage;
  private totalItems;
  public missingPallets$: BehaviorSubject<MissingCrate[]> = new BehaviorSubject<MissingCrate[]>([]);

  constructor(private dataSource: CratesDataSourceService) {
    this.$pageSize.next(this.pageSize);
    this.$pageSizeOptions.next(this.pageSizeOptions);
  }

  getMissingPallets(): Observable<MissingCrate[]> {
    return this.missingPallets$.asObservable();
  }

  async init(filter: MissingCrateFilterParams = null) {
    return this.getPage(1, filter);
  }

  async reload(filter: MissingCrateFilterParams = null, sortParams: MissingCrateSortParams = null) {
    return await this.getPage(this.currentPage, filter, sortParams);
  }

  private async getPage(page: number, filter: MissingCrateFilterParams = {}, sortParams: MissingCrateSortParams = null) {
    this.currentPage = page;

    const params = {
      limit: this.pageSize,
      offset: (this.currentPage - 1) * this.pageSize
    };
    Object.assign(params, sortParams);
    Object.assign(params, filter);
    const response = await this.dataSource.getMissingCrates(params);
    this.totalItems = response.count;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$items.next(response.crates);
    this.$pageCount.next(this.pageCount);
    this.$totalItems.next(this.totalItems);
    this.$currentPage.next(this.currentPage);
    this.$atLastPage.next(this.pageCount === 0 || this.currentPage === this.pageCount);
    this.$atFirstPage.next(this.currentPage === 1);
  }

  async getNextPage(filter: MissingCrateFilterParams = null, sortParams: MissingCrateSortParams) {
    if (this.currentPage < this.pageCount) {
      return await this.getPage(this.currentPage + 1, filter, sortParams);
    } else {
      throw Error('???');
    }
  }

  async getPreviousPage(filter: MissingCrateFilterParams = null, sortParams: MissingCrateSortParams) {
    if (this.currentPage > 1) {
      return await this.getPage(this.currentPage - 1, filter, sortParams);
    } else {
      throw Error('???');
    }
  }

  async setPageSize(pageSize: number) {
    this.pageSize = pageSize;
    this.pageCount = Math.ceil(this.totalItems / this.pageSize);
    this.$pageSize.next(this.pageSize);
    await this.getPage(1);
  }

  async setPage(page: number, filter: MissingCrateFilterParams = null, sortParams: MissingCrateSortParams) {
    this.currentPage = page;
    return await this.getPage(this.currentPage, filter, sortParams);
  }

  get currentPage$(): Observable<number> {
    return this.$currentPage;
  }
  get atFirstPage$(): Observable<boolean> {
    return this.$atFirstPage;
  }
  get atLastPage$(): Observable<boolean> {
    return this.$atLastPage;
  }
  get totalItems$(): Observable<number> {
    return this.$totalItems;
  }
  get pageCount$(): Observable<number> {
    return this.$pageCount;
  }
  get items$(): Observable<any[]> {
    return this.$items;
  }

  get pageSize$(): Observable<number> {
    return this.$pageSize;
  }

  get pageSizeOptions$(): Observable<number[]> {
    return this.$pageSizeOptions;
  }


}
